// exclude-from-index
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import reactStringReplace from 'react-string-replace';
import Adaptive from '../../Adaptive';
import Button from '../../BuildingBlocks/Button';
import Grid from '../../Utility/Grid';
import Text from '../../BuildingBlocks/Text';
import Checkbox from '../../FormElements/Checkbox/Checkbox';

import {
  StyledSummaryWrapper,
  StyledBoldText,
  StyledDoubleColumn,
  StyledCostColumn,
  StyledTotalPrice,
  StyledCurrencyLine,
  StyledBookNowColumn,
  StyledBookingLabel,
  StyledMobileButton,
  StyledMiniCurrencyLine,
  StyledMobileTotalPrice,
  StyledEstimatedLabel,
  StyledDesktopSummaryWrapper,
  StyledSummaryDesktopBackground,
  StyledSummaryText,
  StyledCapacityWrapper,
  StyledCapacityText,
  StyledNoteText,
  StyledEstimatedLabelContainer,
  StyledBookNowButton,
  AlbertaTaxNotification,
  NotificationCircle
} from './Summary.styles';

import { messages } from './messages';

class Summary extends Component {
  static propTypes = {
    totalPrice: PropTypes.string,
    capacity: PropTypes.number,
    /** Number of rooms entered - has to be a string */
    numRooms: PropTypes.number,
    onClick: PropTypes.func,
    rooms: PropTypes.number,
    nights: PropTypes.number,
    restrictPastCheckin: PropTypes.bool,
    propertyPane: PropTypes.bool,
    labels: PropTypes.shape({
      bookingButton: PropTypes.string,
      capacity: PropTypes.string,
      template: PropTypes.string,
      estimatedCost: PropTypes.string,
      currency: PropTypes.string,
      mobileCurrency: PropTypes.string,
      singularRoom: PropTypes.string,
      multipleRooms: PropTypes.string,
      singularNight: PropTypes.string,
      multipleNights: PropTypes.string
    }),
    canCreateBooking: PropTypes.bool,
    canCreateMultiGuestBooking: PropTypes.bool
  };

  static defaultProps = {
    totalPrice: undefined,
    capacity: 0,
    numRooms: 0,
    rooms: 0,
    nights: 0,
    onClick: () => {},
    restrictPastCheckin: false,
    propertyPane: false,
    canCreateBooking: false,
    canCreateMultiGuestBooking: true,
    labels: {
      permissionDenied:
        'Your account does not allow for booking permissions. Please contact your administrator to perform this booking on your behalf.',
      bookingButton: 'BOOK NOW',
      capacity: 'Max Capacity',
      template: 'You have {rooms} {roomLabel} selected for {nights} {nightLabel}',
      estimatedCost: 'Estimated cost',
      currency: 'CAD/Night',
      mobileCurrency: 'CAD',
      singularRoom: '',
      multipleRooms: '',
      singularNight: '',
      multipleNights: ''
    }
  };

  constructor(props) {
    super(props);
    this.renderSummaryString = this.renderSummaryString.bind(this);
    this.renderMobileSummary = this.renderMobileSummary.bind(this);
    this.renderDesktopSummary = this.renderDesktopSummary.bind(this);
  }

  isDisabled() {
    const { rooms, numRooms, bookingBypassReasonId, restrictPastCheckin } = this.props;

    if (restrictPastCheckin) {
      return true;
    }

    if (bookingBypassReasonId && rooms > 0) {
      return false;
    }

    return numRooms > rooms || rooms === 0;
  }

  renderSummaryString() {
    const { labels, rooms, nights } = this.props;
    const roomLabel = rooms === 1 ? labels.singularRoom : labels.multipleRooms;
    const nightLabel = nights === 1 ? labels.singularNight : labels.multipleNights;
    const grammaticallyCorrectString = labels.template
      .replace('{rooms}', rooms)
      .replace('{roomLabel}', roomLabel)
      .replace('{nights}', nights)
      .replace('{nightLabel}', nightLabel);
    const boldString = reactStringReplace(grammaticallyCorrectString, /(\d+)/g, (match, index) => (
      <StyledBoldText key={`room-key-${index}`}>{match}</StyledBoldText>
    ));

    return boldString;
  }

  renderMobileSummary() {
    const { labels, totalPrice, onClick, capacity } = this.props;
    return (
      <>
        <StyledSummaryWrapper>
          <Text.CopyBody className="summary-string" noMargins align="center">
            {this.renderSummaryString()}
          </Text.CopyBody>
          <Text.CopyParagraph noMargins align="center">
            {labels.capacity}:<StyledBoldText>{capacity}</StyledBoldText>
          </Text.CopyParagraph>
        </StyledSummaryWrapper>
        <StyledMobileButton fullWidth onClick={onClick} disabled={this.isDisabled()}>
          <div>
            <div>
              <Text.CopyMini align="left" color="secondary" noMargins>
                {labels.estimatedCost}
              </Text.CopyMini>
              <StyledMobileTotalPrice noMargins color="secondary">
                {totalPrice}
              </StyledMobileTotalPrice>
              <StyledMiniCurrencyLine color="secondary">{labels.mobileCurrency}</StyledMiniCurrencyLine>
            </div>
            <div>
              <StyledNoteText>{labels.maxOccupancyNote}</StyledNoteText>
            </div>
            <div>
              <StyledBookingLabel color="secondary">{labels.bookingButton}</StyledBookingLabel>
            </div>
          </div>
        </StyledMobileButton>
      </>
    );
  }

  renderPropertyPaneSummary() {
    const {
      labels,
      totalPrice,
      onClick,
      capacity,
      propertyPane,
      isBlockBooking,
      showBlockBookingToggle,
      toggleBlockBooking,
      shouldDisplayAlbertaTaxNotification,
      canCreateBooking,
      canCreateMultiGuestBooking
    } = this.props;

    return (
      <>
        <StyledDesktopSummaryWrapper propertyPane={propertyPane}>
          <StyledSummaryDesktopBackground propertyPane={propertyPane}>
            <StyledSummaryText className="summary-string">{this.renderSummaryString()}</StyledSummaryText>
            <StyledCapacityWrapper>
              <StyledCapacityText>
                {labels.capacity}: <StyledBoldText>{capacity}</StyledBoldText>
              </StyledCapacityText>
            </StyledCapacityWrapper>
            <Grid.Container nogutter>
              <StyledDoubleColumn>
                <StyledCostColumn xs={12} sm={12}>
                  <StyledEstimatedLabel color="tertiary" noMargins>
                    {labels.estimatedCost}
                  </StyledEstimatedLabel>
                  <StyledTotalPrice noMargins>{totalPrice}</StyledTotalPrice>
                  <StyledCurrencyLine noMargins color="tertiary">
                    {labels.currency}
                  </StyledCurrencyLine>
                </StyledCostColumn>
                <StyledBookNowColumn xs={12} sm={12} propertyPane={propertyPane}>
                  <StyledNoteText>{labels.maxOccupancyNote}</StyledNoteText>
                </StyledBookNowColumn>
              </StyledDoubleColumn>
            </Grid.Container>
            {shouldDisplayAlbertaTaxNotification && (
              <AlbertaTaxNotification>
                <NotificationCircle />
                <Text.CopyParagraph noMargins weight="bold">
                  {messages.taxNotification}
                </Text.CopyParagraph>
              </AlbertaTaxNotification>
            )}
          </StyledSummaryDesktopBackground>
        </StyledDesktopSummaryWrapper>
        <StyledBookNowButton>
          {!canCreateBooking && (
            <Text.CopyMini tagName="div" color="error" noMargins>
              {labels.permissionDenied}
            </Text.CopyMini>
          )}
          {!canCreateMultiGuestBooking && canCreateBooking ? (
            <Text.CopyMini tagName="div" color="error" noMargins>
              {labels.restrictedRooms}
            </Text.CopyMini>
          ) : null}
          {showBlockBookingToggle && (
            <Checkbox
              checked={isBlockBooking}
              onChange={toggleBlockBooking}
              customLabel={
                <Text.CopyBody color="tertiary" noMargins fontSize={14}>
                  {labels.blockBooking}
                </Text.CopyBody>
              }
              noLabelMargins
            />
          )}
          <Button fullWidth onClick={onClick} disabled={this.isDisabled()}>
            {labels.bookingButton}
          </Button>
        </StyledBookNowButton>
      </>
    );
  }

  renderDesktopSummary() {
    const {
      labels,
      totalPrice,
      onClick,
      capacity,
      toggleBlockBooking,
      showBlockBookingToggle,
      isBlockBooking,
      canCreateBooking,
      canCreateMultiGuestBooking,
      shouldDisplayAlbertaTaxNotification
    } = this.props;

    return (
      <StyledDesktopSummaryWrapper>
        <StyledSummaryDesktopBackground>
          <StyledSummaryText className="summary-string">{this.renderSummaryString()}</StyledSummaryText>
          <StyledCapacityWrapper>
            <StyledCapacityText>
              {labels.capacity}: <StyledBoldText>{capacity}</StyledBoldText>
            </StyledCapacityText>
          </StyledCapacityWrapper>
          <Grid.Container nogutter>
            <StyledDoubleColumn>
              <StyledCostColumn xs={12} sm={12}>
                <StyledTotalPrice noMargins>{totalPrice}</StyledTotalPrice>
                <StyledCurrencyLine noMargins color="tertiary">
                  {labels.currency}
                </StyledCurrencyLine>
              </StyledCostColumn>
              <StyledEstimatedLabelContainer>
                <StyledEstimatedLabel color="tertiary" noMargins>
                  {labels.estimatedCost}
                </StyledEstimatedLabel>
              </StyledEstimatedLabelContainer>
              <StyledBookNowColumn xs={12} sm={12}>
                <StyledNoteText>{labels.maxOccupancyNote}</StyledNoteText>
                {!canCreateBooking && (
                  <Text.CopyMini tagName="div" color="error" noMargins>
                    {labels.permissionDenied}
                  </Text.CopyMini>
                )}
                {!canCreateMultiGuestBooking && canCreateBooking ? (
                  <Text.CopyMini tagName="div" color="error" noMargins>
                    {labels.restrictedRooms}
                  </Text.CopyMini>
                ) : null}
                {showBlockBookingToggle && (
                  <Checkbox
                    checked={isBlockBooking}
                    onChange={toggleBlockBooking}
                    customLabel={
                      <Text.CopyBody color="tertiary" noMargins fontSize={14}>
                        {labels.blockBooking}
                      </Text.CopyBody>
                    }
                    noLabelMargins
                  />
                )}
                {shouldDisplayAlbertaTaxNotification && (
                  <AlbertaTaxNotification>
                    <NotificationCircle />
                    <Text.CopyParagraph noMargins weight="bold">
                      {messages.taxNotification}
                    </Text.CopyParagraph>
                  </AlbertaTaxNotification>
                )}
                <Button fullWidth onClick={onClick} disabled={this.isDisabled()}>
                  {labels.bookingButton}
                </Button>
              </StyledBookNowColumn>
            </StyledDoubleColumn>
          </Grid.Container>
        </StyledSummaryDesktopBackground>
      </StyledDesktopSummaryWrapper>
    );
  }

  render() {
    const { propertyPane } = this.props;
    return (
      <Adaptive
        sm={this.renderMobileSummary()}
        lg={propertyPane ? this.renderPropertyPaneSummary() : this.renderDesktopSummary()}
      />
    );
  }
}

export default Summary;
